import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Switch,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestoreFromTrash as TrashIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DescountService from '../../services/descount.service';
import { useTranslation } from '../../hooks/useTranslation';

const DescountList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [descounts, setDescounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [togglingId, setTogglingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDescounts();
  }, []);

  const fetchDescounts = async () => {
    setLoading(true);
    try {
      const response = await DescountService.getAll();
      setDescounts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching descounts:', error);
      showSnackbar(t('descounts.errorLoading') + ': ' + (error.response?.data?.message || error.message), 'error');
      setDescounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await DescountService.delete(deleteDialog.id);
      showSnackbar(t('descounts.deletedSuccess'));
      fetchDescounts();
    } catch (error) {
      showSnackbar(t('descounts.errorDeleting'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const handleToggleStatus = async (descount) => {
    const current = descount.status || 'Active';
    const newStatus = current === 'Active' ? 'Inactive' : 'Active';
    setTogglingId(descount.id);
    try {
      await DescountService.update(descount.id, { status: newStatus });
      showSnackbar(
        newStatus === 'Active'
          ? t('descounts.activatedSuccess')
          : t('descounts.deactivatedSuccess')
      );
      fetchDescounts();
    } catch (error) {
      showSnackbar(t('descounts.errorUpdating'), 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredDescounts = descounts.filter(d =>
    d.porcentaje?.toString().includes(searchTerm) ||
    d.monto?.toString().includes(searchTerm)
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('descounts.title')}</Typography>
            <Box display="flex" gap={1}>
              {hasPermission('descuento.list') && (
                <Button
                  variant="outlined"
                  startIcon={<TrashIcon />}
                  onClick={() => navigate('/descounts/deleted')}
                >
                  {t('descounts.deletedDescounts')}
                </Button>
              )}
              {hasPermission('descuento.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/descounts/new')}
                >
                  {t('descounts.newDescount')}
                </Button>
              )}
            </Box>
          </Box>

          <TextField
            placeholder={t('descounts.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('descounts.percentage')}</TableCell>
                  <TableCell>{t('descounts.amount')}</TableCell>
                  <TableCell>{t('descounts.startDate')}</TableCell>
                  <TableCell>{t('descounts.endDate')}</TableCell>
                  <TableCell>{t('students.createdAt')}</TableCell>
                  <TableCell align="center">{t('descounts.status')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredDescounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {t('descounts.noDescountsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDescounts.map((descount) => (
                    <TableRow key={descount.id} hover>
                      <TableCell>{descount.porcentaje}%</TableCell>
                      <TableCell>{descount.monto}</TableCell>
                      <TableCell>{formatDate(descount.start_date)}</TableCell>
                      <TableCell>{formatDate(descount.end_date)}</TableCell>
                      <TableCell>{formatDate(descount.created_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Tooltip
                            title={
                              (descount.status || 'Active') === 'Active'
                                ? t('descounts.deactivate')
                                : t('descounts.activate')
                            }
                          >
                            <Switch
                              checked={(descount.status || 'Active') === 'Active'}
                              onChange={() => handleToggleStatus(descount)}
                              disabled={togglingId === descount.id}
                              color="primary"
                              size="small"
                            />
                          </Tooltip>
                          <Chip
                            label={t(`descounts.status${(descount.status || 'Active') === 'Active' ? 'Active' : 'Inactive'}`)}
                            size="small"
                            color={(descount.status || 'Active') === 'Active' ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ minWidth: 70 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('descuento.create') && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/descounts/${descount.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {hasPermission('descuento.create') && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(descount.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('descounts.deleteDescount')}</DialogTitle>
        <DialogContent>
          <Typography>{t('descounts.deleteConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DescountList;
